import styled from 'styled-components';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  TbTrash,
  TbSearch,
  TbX,
  TbSettings,
  TbChevronDown,
  TbShieldCheck,
  TbEye,
  TbCircleOff,
  TbUser,
  TbMail,
  TbClock,
  TbCircleCheck,
  TbLock
} from 'react-icons/tb';
import { TbPlus } from 'react-icons/tb';
import { v4 as uuidv4 } from 'uuid';
import { useGetFundsByPeriodId } from '../../../../services/funds.service';
import SearchInput from './SearchInput';
import {
  useAccessChangeFund,
  useApprovalRequiredAIF,
  useApprovalRequiredAIFM,
  useDeleteApprover,
  useInvite,
  useInvitePending
} from '../../../../services/report.service';
import { useParams } from 'react-router-dom';
import {
  useGetInvitedMembers,
  useGetInvitedMembersDashboard
} from '../../../../services/user.service';
import agentStore from '../../../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../../../components/ChatWidget/constants';
import { useDidUpdate } from '@mantine/hooks';
import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Flex,
  Input,
  Menu,
  Modal,
  SegmentedControl,
  Text,
  TextInput,
  Tooltip
} from '@mantine/core';
import { DeleteConfirmModal } from '../../../../components/DeleteConfirmModal';
import { notifications } from '@mantine/notifications';
import Drawer from '../../../../components/Drawer';
import { extractApiError } from '../../../../utils';
import { validateEmail } from '../../../../utils/form';

const getNotificationLoadingId = () => {
  return notifications.show({
    loading: true,
    title: 'Updating...',
    position: 'bottom-center',
    autoClose: false,
    styles: {
      root: {
        width: '250px',
        margin: '0 auto'
      }
    }
  });
};

const showUpdatedNotification = (id, message) => {
  notifications.update({
    id: id,
    loading: false,
    color: 'green',
    title: message,
    position: 'bottom-center',
    autoClose: 4000,
    styles: {
      root: {
        width: '100%'
      }
    }
  });
};

const showApiErrorNotification = (error, fallback = 'Server error occurred') => {
  notifications.show({
    color: 'red',
    title: extractApiError(error, fallback),
    position: 'bottom-center',
    autoClose: 4000
  });
};

// Demo mode (append ?reviewersDemo=1 to the URL) — seeds fake reviewers/funds/statuses and stubs every
// backend call, so the redesign can be shown end-to-end without hitting the live API. No effect otherwise.
const REVIEWERS_DEMO =
  typeof window !== 'undefined' &&
  (window.location.href.includes('reviewersDemo=1') ||
    (() => {
      try {
        return window.localStorage.getItem('reviewersDemo') === '1';
      } catch {
        return false;
      }
    })());

const DEMO_FUNDS = [
  { id: 'demo-f1', name: 'New UI3' },
  { id: 'demo-f2', name: 'New UI 1, New UI2' }
];

const DEMO_ROWS = [
  {
    name: 'Jane Cooper',
    email: 'jane@acme.com',
    approvalRequired: true,
    aifmApprovalRequired: true,
    fundIds: ['demo-f1', 'demo-f2'],
    id: 'demo-a1',
    isAifmIncluded: true,
    approverId: 'demo-a1',
    invitationSent: true,
    isNewMember: false
  },
  {
    name: 'Wade Warren',
    email: 'wade@acme.com',
    approvalRequired: true,
    aifmApprovalRequired: false,
    fundIds: ['demo-f1', 'demo-f2'],
    id: 'demo-a2',
    isAifmIncluded: false,
    approverId: 'demo-a2',
    invitationSent: true,
    isNewMember: false
  }
];

const DEMO_DASHBOARD = [
  {
    approver_id: 'demo-a1',
    fund_ids: [
      { fund_id: 'demo-f1', fund_name: 'New UI3', is_approved: true },
      { fund_id: 'demo-f2', fund_name: 'New UI 1, New UI2', is_approved: true }
    ],
    is_aifm_approved: true
  },
  {
    approver_id: 'demo-a2',
    fund_ids: [
      { fund_id: 'demo-f1', fund_name: 'New UI3', is_approved: true },
      { fund_id: 'demo-f2', fund_name: 'New UI 1, New UI2', is_approved: false }
    ],
    is_aifm_approved: false
  }
];

export function AddApprover({ isOpen, onClose, periodId, isFormPF, isLocked = false }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletableMemberId, setDeletableMemberId] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  // Approver IDs the user has checked for the "Send drafts" batch. Only draft
  // rows (persisted, invitation not yet sent) are checkable; the set is
  // cleared automatically when there are no drafts left so it can't retain
  // stale IDs across a members refetch.
  const [selectedDraftIds, setSelectedDraftIds] = useState([]);
  const [isSendImmediately, setisSendImmediately] = useState(false);
  const invite = useInvite();
  const invitePending = useInvitePending();
  const deleteApprover = useDeleteApprover();
  const approvalRequiredAIF = useApprovalRequiredAIF();
  const approvalRequiredAIFM = useApprovalRequiredAIFM();
  const accessChangeFund = useAccessChangeFund();
  const { id, workflowId } = useParams();
  const [term, setTerm] = useState(null);
  const [fundIds, setFundIds] = useState([]);
  // Fund search is hidden behind an icon for short lists (rarely needed); shown inline when there are many.
  const [showFundSearch, setShowFundSearch] = useState(false);
  // Per-fund access level for the currently-open reviewer:
  //   { [fundId]: 'none' | 'view' | 'approve' }
  // Funds missing from the map are treated as 'none' (not assigned). This is
  // the primary source of truth for the fund table — checkbox state, level
  // segmented control, status column and Save diff all read from here.
  const [fundLevels, setFundLevels] = useState({});
  // Snapshot of each existing reviewer's server-side state at hydration time,
  // used for (a) row-dirty detection and (b) Cancel restore. Keyed by
  // approver_id. Populated in `useGetInvitedMembers.onSuccess`.
  const originalStateRef = useRef({});
  // Save spinner + close-drawer prompt state.
  const [savingRow, setSavingRow] = useState(null);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  const [rows, setRows] = useState([
    {
      name: '',
      email: '',
      approvalRequired: false,
      aifmApprovalRequired: false,
      fundIds: [],
      id: uuidv4(),
      isAifmIncluded: false,
      isNewMember: true,
      invitationSent: false
    }
  ]);
  const inputFundsQuery = useGetFundsByPeriodId({
    periodId: periodId,
    queryParams: {
      enabled: !!periodId && isOpen && !REVIEWERS_DEMO
    }
  });
  const inputFunds = REVIEWERS_DEMO ? { data: DEMO_FUNDS } : inputFundsQuery;
  const inputFundsData = Array.isArray(inputFunds?.data) ? inputFunds.data : [];

  const { refetch: refetchMembers } = useGetInvitedMembers({
    params: {
      period_id: periodId,
      workflow_id: id || workflowId
    },
    queryOptions: {
      enabled: !!periodId && !REVIEWERS_DEMO,
      onSuccess: res => {
        if (REVIEWERS_DEMO) return;
        if (res?.length > 0) {
          // Backend returns `fund_ids` as an array of objects:
          //   [{ fund_id, fund_name, is_approved, is_approve_required }, …]
          // Read per-fund `is_approve_required` when present; fall back to
          // the reviewer-wide flag for the (old) string-only shape.
          const extractFundId = f => (typeof f === 'string' ? f : f?.fund_id);
          const buildLevels = item => {
            const out = {};
            const rowLevel = item.is_approve_required ? 'approve' : 'view';
            (item.fund_ids || []).forEach(f => {
              const fid = extractFundId(f);
              if (!fid) return;
              const perFundApprove =
                typeof f === 'object' && f?.is_approve_required != null
                  ? f.is_approve_required
                  : null;
              out[fid] = perFundApprove === null ? rowLevel : perFundApprove ? 'approve' : 'view';
            });
            return out;
          };
          // Flat string array of assigned fund ids, kept for legacy
          // consumers (`hasNoAccess`, `handleSelectFunds`, send-invite body).
          const extractFundIds = item => (item.fund_ids || []).map(extractFundId).filter(Boolean);
          originalStateRef.current = Object.fromEntries(
            res.map(item => [
              item.approver_id,
              {
                fundLevels: buildLevels(item),
                isAifmIncluded: !!item.is_aifm_included,
                aifmApprovalRequired: !!item.is_aifm_approve_required
              }
            ])
          );
          setRows(
            res.map(item => ({
              name: `${item.first_name} ${item.last_name}`,
              email: item.email,
              approvalRequired: item.is_approve_required,
              // Store as string[] so legacy consumers (hasNoAccess,
              // handleSelectFunds, send-invite payload) don't need to know
              // about the object shape.
              fundIds: extractFundIds(item),
              // Row-local fundLevels — mirrored to `fundLevels` state when the
              // row is opened, and read back on close/save.
              fundLevels: buildLevels(item),
              id: item.approver_id,
              isAifmIncluded: item.is_aifm_included,
              aifmApprovalRequired: item.is_aifm_approve_required,
              approverId: item.approver_id,
              invitationSent: item.invitation_sent,
              isNewMember: false
            }))
          );
        } else {
          setRows([
            {
              name: '',
              email: '',
              approvalRequired: false,
              aifmApprovalRequired: false,
              fundIds: [],
              id: uuidv4(),
              isAifmIncluded: false,
              isNewMember: true,
              invitationSent: false
            }
          ]);
        }
      }
    }
  });

  const onChangeTerm = value => {
    setTerm(value);
  };

  const onAddRow = () => {
    setRows(prev => [
      ...prev,
      {
        name: '',
        email: '',
        approvalRequired: false,
        aifmApprovalRequired: false,
        fundIds: [],
        id: uuidv4(),
        isAifmIncluded: false,
        isNewMember: true,
        invitationSent: false
      }
    ]);
  };

  const onSelectUser = member => {
    if (selectedUsers.find(item => item.id === member.id)) {
      setSelectedUsers(prev => prev.filter(value => value.id !== member.id));
    } else {
      setSelectedUsers(prev => [...prev, member]);
    }
  };

  const onSelectAllUsers = () => {
    if (selectedUsers.length === rows.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(rows);
    }
  };

  const checkedUser = member => selectedUsers.find(item => item.id === member.id);

  const onDeleteRow = user => {
    if (user.approverId && !REVIEWERS_DEMO) {
      setDeletableMemberId(user.approverId);
    } else {
      setRows(prev => prev.filter(item => item.id !== user.id));
    }
  };

  const onChange = (e, id) => {
    const _rows = rows.map(item =>
      item.id === id
        ? {
            ...item,
            [e.target.name]: e.target.value
          }
        : item
    );
    setRows(_rows);
  };

  const onEnableApprove = user => {
    if (user.approverId && !REVIEWERS_DEMO) {
      const notId = getNotificationLoadingId();
      approvalRequiredAIF.mutate(
        {
          params: {
            workflow_id: id || workflowId,
            period_id: periodId,
            approver_id: user.approverId
          },
          body: {
            is_required: !user.approvalRequired
          }
        },

        {
          onSuccess: res => {
            showUpdatedNotification(notId, res?.message);
            refetchMembers();
          },
          onError: () => {
            notifications.clean();
          }
        }
      );
    } else {
      const _rows = rows.map(item =>
        item.id === user.id
          ? {
              ...item,
              approvalRequired: !item.approvalRequired
            }
          : item
      );
      setRows(_rows);
    }
  };

  const onEnableApproveAIFM = user => {
    if (user.approverId && !REVIEWERS_DEMO) {
      const notId = getNotificationLoadingId();
      approvalRequiredAIFM.mutate(
        {
          params: {
            workflow_id: id || workflowId,
            period_id: periodId,
            approver_id: user.approverId
          },
          body: {
            is_required: !user.aifmApprovalRequired
          }
        },
        {
          onSuccess: res => {
            showUpdatedNotification(notId, res?.message);
            refetchMembers();
          },
          onError: () => {
            notifications.clean();
          }
        }
      );
    } else {
      const _rows = rows.map(item =>
        item.id === user.id
          ? {
              ...item,
              aifmApprovalRequired: !item.aifmApprovalRequired,
              isAifmIncluded: !item.aifmApprovalRequired ? true : item.isAifmIncluded
            }
          : item
      );
      setRows(_rows);
    }
  };

  const updateSelectedUserFunds = (
    nextFundIds,
    nextIsAifmIncluded = selectedUser?.isAifmIncluded
  ) => {
    if (!selectedUser) return;

    const savedFundIds = [...nextFundIds];
    const updatedUser = {
      ...selectedUser,
      isAifmIncluded: nextIsAifmIncluded,
      fundIds: savedFundIds
    };

    setFundIds(savedFundIds);
    setSelectedUser(updatedUser);
    setRows(prev => prev.map(item => (item.id === selectedUser.id ? updatedUser : item)));
    setSelectedUsers(prev => prev.map(item => (item.id === selectedUser.id ? updatedUser : item)));
  };

  const onSelectFund = id => {
    const nextFundIds = fundIds.includes(id) ? fundIds.filter(val => val !== id) : [...fundIds, id];
    updateSelectedUserFunds(nextFundIds);
  };

  const onSaveFunds = () => {
    setSelectedUser(null);
    setFundIds([]);
  };

  const newMembers = rows.filter(item => item.isNewMember);

  // Approval progress (per-fund is_approved, is_aifm_approved) lives in the dashboard endpoint; merge
  // it into the rows by approver_id so each row can show its status inline.
  const membersDashboardQuery = useGetInvitedMembersDashboard({
    params: { period_id: periodId, workflow_id: id || workflowId },
    queryOptions: { enabled: !!periodId && isOpen && !REVIEWERS_DEMO }
  });
  const membersDashboard = REVIEWERS_DEMO ? { data: DEMO_DASHBOARD } : membersDashboardQuery;
  const statusById = useMemo(() => {
    const map = {};
    (membersDashboard.data || []).forEach(m => {
      map[m.approver_id] = m;
    });
    return map;
  }, [membersDashboard.data]);

  // Seed demo reviewers once when the demo modal opens.
  useEffect(() => {
    if (REVIEWERS_DEMO && isOpen) setRows(DEMO_ROWS);
  }, [isOpen]);

  // Small ring that fills to value/total (inherits the badge's currentColor).
  const progressRing = (value, total) => {
    const c = 2 * Math.PI * 6;
    const frac = total > 0 ? Math.min(value / total, 1) : 0;
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" style={{ display: 'block' }}>
        <circle
          cx="8"
          cy="8"
          r="6"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.3"
          strokeWidth="2.5"
        />
        <circle
          cx="8"
          cy="8"
          r="6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${frac * c} ${c}`}
          transform="rotate(-90 8 8)"
        />
      </svg>
    );
  };

  const _inputFunds = useMemo(() => {
    if (term) {
      return inputFunds.data?.filter(item =>
        item.name.toLocaleLowerCase().includes(term.toLocaleLowerCase())
      );
    }
    return inputFunds.data;
  }, [inputFunds.data, term]);

  const getSelectedReviewItemsCount = user =>
    (user?.fundIds?.length || 0) + (user?.isAifmIncluded ? 1 : 0);

  const getFundAccessLabel = user => {
    const fundCount = user?.fundIds?.length || 0;
    const fundLabel = `${fundCount} ${fundCount === 1 ? 'fund' : 'funds'}`;

    if (user?.isAifmIncluded) return `${fundLabel} + AIFM Report`;
    if (fundCount > 0) return fundLabel;

    return '';
  };

  const onDeleteMember = () => {
    deleteApprover.mutate(
      {
        params: {
          workflow_id: id || workflowId,
          period_id: periodId,
          approver_id: deletableMemberId
        }
      },
      {
        onSuccess: () => {
          setDeletableMemberId(null);
          refetchMembers();
          notifications.show({
            color: 'green',
            title: 'Reviewer has been deleted',
            position: 'bottom-center',
            autoClose: 4000
          });
        },
        onError: e => {
          showApiErrorNotification(e);
        }
      }
    );
  };

  const onSave = async (shouldSendNow = false) => {
    if (REVIEWERS_DEMO) {
      setisSendImmediately(false);
      setRows(prev => prev.map(r => (r.isNewMember ? { ...r, invitationSent: shouldSendNow } : r)));
      return notifications.show({
        color: 'green',
        title: shouldSendNow ? 'Sent for review' : 'Saved as draft',
        position: 'bottom-center',
        autoClose: 3000
      });
    }
    setisSendImmediately(shouldSendNow);

    const _selectedUsers = rows.filter(row => selectedUsers.find(user => user.id === row.id));

    // Build the invite body for a single user. Prefers per-fund levels
    // (`user.fundLevels`) — funds set to 'none' are dropped and each fund
    // carries its own `is_approve_required`. Falls back to the legacy
    // reviewer-wide flag only when `fundLevels` is empty (e.g. the user hit
    // Send without ever opening the per-fund controls). Top-level
    // `is_approve_required` is set true when ANY assigned fund is Approve.
    const buildInvitePayload = user => {
      const levels = user.fundLevels || {};
      const hasPerFund = Object.keys(levels).length > 0;
      const fundIdsPayload = hasPerFund
        ? Object.keys(levels)
            .filter(fid => levels[fid] === 'view' || levels[fid] === 'approve')
            .map(fid => ({
              fund_id: fid,
              is_approve_required: levels[fid] === 'approve'
            }))
        : (user.fundIds || []).map(fund_id => ({
            fund_id,
            is_approve_required: !!user.approvalRequired
          }));
      const anyApprove = hasPerFund
        ? Object.values(levels).some(l => l === 'approve')
        : !!user.approvalRequired;
      return {
        name: user.name,
        email: user.email,
        fund_ids: fundIdsPayload,
        is_aifm_included: !!user.isAifmIncluded,
        is_aifm_approve_required: !!user.aifmApprovalRequired,
        is_approve_required: anyApprove
      };
    };

    if (shouldSendNow) {
      if (_selectedUsers.length === 0) {
        setisSendImmediately(false);
        return notifications.show({
          color: 'red',
          title: 'Please choose at least one reviewer before sending for approval.',
          position: 'bottom-center',
          autoClose: 4000
        });
      }

      const newInvitees = _selectedUsers.filter(
        user => user.isNewMember && user.name && user.email
      );
      const existingApprovers = _selectedUsers.filter(user => user.approverId);
      const draftInvitees = rows.filter(
        row => !checkedUser(row) && row.isNewMember && row.name && row.email
      );

      const promises = [];

      if (newInvitees.length > 0) {
        const p = new Promise(resolve => {
          invite.mutate(
            {
              params: {
                workflow_id: id || workflowId,
                period_id: periodId,
                send_immediately: true
              },
              body: newInvitees.map(buildInvitePayload)
            },
            {
              onSuccess: () => {
                notifications.show({
                  color: 'green',
                  title: 'Invitation has been sent',
                  position: 'bottom-center',
                  autoClose: 4000
                });

                resolve(true);
              },
              onError: error => {
                showApiErrorNotification(error);
                resolve(false);
              }
            }
          );
        });
        promises.push(p);
      }

      if (draftInvitees.length > 0) {
        const p = new Promise(resolve => {
          invite.mutate(
            {
              params: {
                workflow_id: id || workflowId,
                period_id: periodId,
                send_immediately: false
              },
              body: draftInvitees.map(buildInvitePayload)
            },
            {
              onSuccess: () => {
                notifications.show({
                  color: 'green',
                  title: 'Reviewer information has been saved for the later sending',
                  position: 'bottom-center',
                  autoClose: 4000
                });

                resolve(true);
              },
              onError: error => {
                showApiErrorNotification(error);
                resolve(false);
              }
            }
          );
        });
        promises.push(p);
      }

      if (existingApprovers.length > 0) {
        const p = new Promise(resolve => {
          invitePending.mutate(
            {
              params: {
                workflow_id: id || workflowId,
                period_id: periodId
              },
              body: existingApprovers.map(user => user.approverId)
            },
            {
              onSuccess: () => {
                notifications.show({
                  color: 'green',
                  title: 'Invitation has been sent',
                  position: 'bottom-center',
                  autoClose: 4000
                });

                resolve(true);
              },
              onError: error => {
                showApiErrorNotification(error);
                resolve(false);
              }
            }
          );
        });
        promises.push(p);
      }

      await Promise.allSettled(promises);
      refetchMembers();
      setisSendImmediately(false);
      setSelectedUsers([]);
    } else {
      const newInvitees = rows.filter(row => row.isNewMember && row.name && row.email);
      if (newInvitees.length > 0)
        invite.mutate(
          {
            params: {
              workflow_id: id || workflowId,
              period_id: periodId,
              send_immediately: false
            },
            body: newInvitees.map(buildInvitePayload)
          },
          {
            onSuccess: () => {
              refetchMembers();
              notifications.show({
                color: 'green',
                title: 'Reviewer information has been saved for the later sending',
                position: 'bottom-center',
                autoClose: 4000
              });
            },
            onError: error => {
              showApiErrorNotification(error);
            },
            onSettled: () => {
              setisSendImmediately(false);
            }
          }
        );
    }
  };

  const onChangeAIFMInclude = () => {
    updateSelectedUserFunds(fundIds, !selectedUser.isAifmIncluded);
  };

  const onSelectAll = () => {
    if (fundIds.length === inputFundsData.length && selectedUser.isAifmIncluded) {
      updateSelectedUserFunds([], selectedUser.aifmApprovalRequired ? true : false);
    } else {
      updateSelectedUserFunds(
        inputFundsData.map(item => item.id),
        true
      );
    }
  };

  useDidUpdate(() => {
    if (isOpen) {
      agentStore.setCurrentSubScreen(WEB_PAGE_STATE_ENUMS.ApproverDrawer);
    } else {
      agentStore.setCurrentSubScreen('');
    }
  }, [isOpen]);

  const onChangeAccessFund = (fundId, accessGiven) => {
    const notId = getNotificationLoadingId();
    const nextFundIds = accessGiven
      ? fundIds.filter(value => value !== fundId)
      : [...fundIds, fundId];

    accessChangeFund.mutate(
      {
        params: {
          workflow_id: id || workflowId,
          period_id: periodId,
          approver_id: selectedUser.approverId
        },
        body: {
          fund_id: fundId,
          access_given: !accessGiven
        }
      },
      {
        onSuccess: async res => {
          showUpdatedNotification(notId, res?.message);
          updateSelectedUserFunds(nextFundIds);
          await refetchMembers();
        }
      }
    );
  };

  const onChangeAccessAIFM = accessGiven => {
    const notId = getNotificationLoadingId();
    accessChangeFund.mutate(
      {
        params: {
          workflow_id: id || workflowId,
          period_id: periodId,
          approver_id: selectedUser.approverId
        },
        body: {
          is_aifm: true,
          access_given: !accessGiven
        }
      },
      {
        onSuccess: async res => {
          showUpdatedNotification(notId, res?.message);
          updateSelectedUserFunds(fundIds, !accessGiven);
          await refetchMembers();
        }
      }
    );
  };

  // A reviewer with no access at all (no funds AND no AIFM) is meaningless — they should be removed,
  // not sent. Block sending while any row is empty of access.
  const hasNoAccess = user => (user.fundIds?.length || 0) === 0 && !user.isAifmIncluded;

  const disabledSendButton = useMemo(() => {
    return rows.some(user => !validateEmail(user.email) || hasNoAccess(user));
  }, [rows]);

  // Draft approvers = persisted reviewers whose invitation email hasn't
  // been sent yet (`approverId` set + `invitation_sent === false`). Powers
  // both the row-level "Draft" badge and the footer's "Send drafts (N)"
  // button. Fresh unsaved rows (no `approverId`) go through the invite
  // flow instead, not through here.
  const draftApprovers = useMemo(
    () => rows.filter(r => !!r.approverId && !r.invitationSent),
    [rows]
  );

  // Set of draft rows the user has actually ticked for sending. Intersected
  // with the current draft list so IDs left over from a previous refetch
  // (e.g. the draft was already sent) are transparently dropped.
  const selectedDrafts = useMemo(
    () => draftApprovers.filter(r => selectedDraftIds.includes(r.approverId)),
    [draftApprovers, selectedDraftIds]
  );

  const toggleDraftSelection = approverId => {
    setSelectedDraftIds(prev =>
      prev.includes(approverId) ? prev.filter(id => id !== approverId) : [...prev, approverId]
    );
  };

  // Prune selection whenever the draft list shrinks (successful send, member
  // deletion, etc.) so `selectedDraftIds` never holds a stale id.
  useEffect(() => {
    if (selectedDraftIds.length === 0) return;
    const stillDraft = new Set(draftApprovers.map(r => r.approverId));
    const pruned = selectedDraftIds.filter(id => stillDraft.has(id));
    if (pruned.length !== selectedDraftIds.length) setSelectedDraftIds(pruned);
  }, [draftApprovers, selectedDraftIds]);

  // Fires POST /api/invite/pending/send with the *checked* draft approvers'
  // IDs. Reuses the `invitePending` mutation instantiated for the combined
  // Save flow, but scoped strictly to draft rows. On success,
  // `refetchMembers()` flips `invitation_sent` → true on each affected row,
  // dropping the Draft badge; the useEffect above then prunes the selection.
  const handleSendDrafts = () => {
    if (selectedDrafts.length === 0) return;
    const notId = getNotificationLoadingId();
    invitePending.mutate(
      {
        params: {
          workflow_id: id || workflowId,
          period_id: periodId
        },
        body: selectedDrafts.map(r => r.approverId)
      },
      {
        onSuccess: () => {
          showUpdatedNotification(
            notId,
            selectedDrafts.length === 1
              ? 'Draft invitation sent'
              : `${selectedDrafts.length} draft invitations sent`
          );
          refetchMembers();
        },
        onError: err => {
          notifications.clean();
          showApiErrorNotification(err, 'Unable to send draft invitations');
        }
      }
    );
  };

  // Diff a row's current state against the server snapshot. New (unsent)
  // reviewers have no snapshot — they're never dirty (their changes go out
  // via the bottom Send button, not per-row Save).
  const isRowDirty = row => {
    if (!row?.approverId) return false;
    const snap = originalStateRef.current[row.approverId];
    if (!snap) return false;
    // Live source for the open row is `fundLevels` state; other rows carry
    // theirs on `row.fundLevels` (kept in sync via patchRow).
    const cur = row.id === selectedUser?.id ? fundLevels : row.fundLevels || {};
    const curIds = Object.keys(cur);
    const snapIds = Object.keys(snap.fundLevels || {});
    if (curIds.length !== snapIds.length) return true;
    for (const fid of curIds) {
      if (cur[fid] !== snap.fundLevels[fid]) return true;
    }
    return (
      !!row.isAifmIncluded !== snap.isAifmIncluded ||
      !!row.aifmApprovalRequired !== snap.aifmApprovalRequired
    );
  };
  const hasAnyDirty = () => rows.some(isRowDirty);

  // Diff local vs snapshot per fund and fire the delta. Each fund can require
  // up to one `access/fund/change` call:
  //   • level moved off 'none'          → grant with is_approve_required
  //   • level moved onto 'none'         → revoke (access_given: false)
  //   • level changed view ↔ approve    → re-post to flip is_approve_required
  // Sequential so failures surface immediately.
  const saveRow = async row => {
    if (!row?.approverId || savingRow) return;
    const snap = originalStateRef.current[row.approverId];
    if (!snap) return;

    setSavingRow(row.id);
    const notId = getNotificationLoadingId();
    const curLevels = row.id === selectedUser?.id ? fundLevels : row.fundLevels || {};
    const snapLevels = snap.fundLevels || {};
    const params = {
      workflow_id: id || workflowId,
      period_id: periodId,
      approver_id: row.approverId
    };

    try {
      const allFundIds = new Set([...Object.keys(curLevels), ...Object.keys(snapLevels)]);
      for (const fid of allFundIds) {
        const cur = curLevels[fid]; // undefined = none
        const prev = snapLevels[fid]; // undefined = none
        if (cur === prev) continue;
        if (!cur) {
          // Went to none → revoke access.
          await accessChangeFund.mutateAsync({
            params,
            body: { fund_id: fid, access_given: false }
          });
        } else {
          // New assignment or level flip — grant + carry is_approve_required.
          await accessChangeFund.mutateAsync({
            params,
            body: {
              fund_id: fid,
              access_given: true,
              is_approve_required: cur === 'approve'
            }
          });
        }
      }

      // AIFM access + approval flag — same shape as before.
      if (!!row.isAifmIncluded !== snap.isAifmIncluded) {
        await accessChangeFund.mutateAsync({
          params,
          body: { is_aifm: true, access_given: !!row.isAifmIncluded }
        });
      }
      if (!!row.aifmApprovalRequired !== snap.aifmApprovalRequired) {
        await approvalRequiredAIFM.mutateAsync({
          params,
          body: { is_required: !!row.aifmApprovalRequired }
        });
      }

      showUpdatedNotification(notId, 'Reviewer updated');
      await refetchMembers();
    } catch (err) {
      notifications.clean();
      showApiErrorNotification(err, 'Unable to save reviewer changes');
    } finally {
      setSavingRow(null);
    }
  };

  // Undo local edits — reset the row and (if open) local fund state to snapshot.
  const cancelRow = row => {
    if (!row?.approverId) return;
    const snap = originalStateRef.current[row.approverId];
    if (!snap) return;
    patchRow(row, () => ({
      ...row,
      fundLevels: { ...snap.fundLevels },
      fundIds: Object.keys(snap.fundLevels || {}),
      approvalRequired: Object.values(snap.fundLevels || {}).some(l => l === 'approve'),
      isAifmIncluded: snap.isAifmIncluded,
      aifmApprovalRequired: snap.aifmApprovalRequired
    }));
  };

  // Drawer close guard — prompt when unsaved edits exist. Discard restores
  // every dirty row to the server snapshot before closing.
  const handleClose = () => {
    if (hasAnyDirty()) {
      setCloseConfirmOpen(true);
      return;
    }
    onClose();
  };
  const confirmCloseDiscard = () => {
    rows.forEach(r => {
      if (isRowDirty(r)) cancelRow(r);
    });
    setCloseConfirmOpen(false);
    onClose();
  };

  useEffect(() => {
    setSelectedUsers(rows.filter(r => r.isNewMember));
  }, [rows]);

  // New reviewers default to the common case — all funds + AIFM report, must approve — so the row
  // reads "All funds · must approve" out of the box (Customize narrows it). Runs once per new row
  // (guarded by _init) so deselecting funds isn't undone.
  useEffect(() => {
    if (!inputFunds.data?.length) return;
    const allIds = inputFunds.data.map(f => f.id);
    const allApproveLevels = Object.fromEntries(allIds.map(fid => [fid, 'approve']));
    setRows(prev => {
      let changed = false;
      const next = prev.map(r => {
        if (r.isNewMember && !r._init) {
          changed = true;
          return {
            ...r,
            _init: true,
            fundIds: allIds,
            // Seed the per-fund level map too. Without this the row's
            // fundLevels starts empty, so the first single-fund change causes
            // patchRow to collapse the drawer's fundLevels state down to just
            // the edited fund (every other fund then reads as 'none' from the
            // `fundLevels[f.id] || 'none'` fallback).
            fundLevels: { ...allApproveLevels },
            isAifmIncluded: true,
            approvalRequired: true,
            aifmApprovalRequired: true
          };
        }
        return r;
      });
      return changed ? next : prev;
    });
  }, [inputFunds.data, rows]);

  const handleSelectFunds = item => {
    setSelectedUser(item);
    // Level source of truth on open: prefer row.fundLevels (set on hydrate),
    // fall back to deriving from fundIds + reviewer-wide approvalRequired for
    // rows that predate the per-fund model.
    const nextLevels = (() => {
      if (item.fundLevels && Object.keys(item.fundLevels).length > 0) {
        return { ...item.fundLevels };
      }
      const lvl = item.approvalRequired ? 'approve' : 'view';
      return Object.fromEntries((item.fundIds || []).map(fid => [fid, lvl]));
    })();
    setFundLevels(nextLevels);
    setFundIds([...(item.fundIds || [])]);
    setShowFundSearch(false);
    setTerm(null);
  };

  // Access level per report part, derived from the backend flags.
  // AIF:  no funds → 'none'; funds + approval → 'approve'; funds, no approval → 'view'.
  // AIFM: not included → 'none'; included + approval → 'approve'; included, no approval → 'view'.
  const getAifLevel = item =>
    (item.fundIds?.length || 0) === 0 ? 'none' : item.approvalRequired ? 'approve' : 'view';
  const getAifmLevel = item =>
    !item.isAifmIncluded ? 'none' : item.aifmApprovalRequired ? 'approve' : 'view';

  const patchRow = (item, upd) => {
    setRows(prev => prev.map(r => (r.id === item.id ? upd(r) : r)));
    setSelectedUsers(prev => prev.map(r => (r.id === item.id ? upd(r) : r)));
    if (selectedUser?.id === item.id) {
      const next = upd({ ...selectedUser });
      setSelectedUser(next);
      setFundIds([...(next.fundIds || [])]);
      if (next.fundLevels) setFundLevels({ ...next.fundLevels });
    }
  };

  // Sync a fund-level change into local state: updates `fundLevels`, mirrors
  // to `fundIds` (kept for legacy call sites — send-invite, snapshot compat),
  // and pushes the update into the row's `fundLevels` so hydration on next
  // open matches. `level === 'none'` removes the fund from the assignment.
  const setFundLevel = (fundId, level) => {
    setFundLevels(prev => {
      const next = { ...prev };
      if (level === 'none') delete next[fundId];
      else next[fundId] = level;
      return next;
    });
    setFundIds(prev => {
      if (level === 'none') return prev.filter(x => x !== fundId);
      return prev.includes(fundId) ? prev : [...prev, fundId];
    });
    if (!selectedUser) return;
    patchRow(selectedUser, r => {
      const nextLevels = { ...(r.fundLevels || {}) };
      if (level === 'none') delete nextLevels[fundId];
      else nextLevels[fundId] = level;
      const nextIds =
        level === 'none'
          ? (r.fundIds || []).filter(x => x !== fundId)
          : (r.fundIds || []).includes(fundId)
            ? r.fundIds || []
            : [...(r.fundIds || []), fundId];
      return { ...r, fundLevels: nextLevels, fundIds: nextIds };
    });
  };

  // Apply the same level to a list of funds (preset "All approve" / bulk
  // toolbar / etc.). Single patchRow so React batches the state updates.
  const setFundsBulk = (fundIdList, level) => {
    if (!selectedUser || !fundIdList?.length) return;
    setFundLevels(prev => {
      const next = { ...prev };
      fundIdList.forEach(fid => {
        if (level === 'none') delete next[fid];
        else next[fid] = level;
      });
      return next;
    });
    setFundIds(prev => {
      const set = new Set(prev);
      fundIdList.forEach(fid => (level === 'none' ? set.delete(fid) : set.add(fid)));
      return [...set];
    });
    patchRow(selectedUser, r => {
      const nextLevels = { ...(r.fundLevels || {}) };
      const nextIds = new Set(r.fundIds || []);
      fundIdList.forEach(fid => {
        if (level === 'none') {
          delete nextLevels[fid];
          nextIds.delete(fid);
        } else {
          nextLevels[fid] = level;
          nextIds.add(fid);
        }
      });
      return { ...r, fundLevels: nextLevels, fundIds: [...nextIds] };
    });
  };

  // Apply a preset level to *every* fund on this workflow. Powers the Presets
  // menu ("Approve all" / "View all" / "None all") for the open reviewer.
  const applyPreset = level => {
    const allIds = inputFunds.data?.map(f => f.id) || [];
    setFundsBulk(allIds, level);
  };

  // Set the AIFM access level. Local-only; Save button fires the delta.
  const setAifmLevel = (item, level) => {
    patchRow(item, r =>
      level === 'none'
        ? { ...r, isAifmIncluded: false, aifmApprovalRequired: false }
        : { ...r, isAifmIncluded: true, aifmApprovalRequired: level === 'approve' }
    );
  };

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={handleClose}
        size={window.innerWidth < 1100 ? '94%' : '1040px'}
        centered
        title="Reviewers and approvals"
      >
        <BodyStyle>
          <div className="rev-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {/* Locked-period banner — automation is locked, so every
                mutation control below is disabled / hidden. Status readouts
                (approval progress, per-fund status dots, badges) still
                render so the user can see who's approved what. Matches the
                pre-redesign contract where ApproverDetails hid its "Edit
                Reviewers" button when isLocked. */}
            {isLocked && (
              <Flex
                align="center"
                gap="8px"
                mb="12px"
                p="10px 12px"
                style={{
                  background: '#fff9db',
                  border: '1px solid #ffe066',
                  borderRadius: 8
                }}
              >
                <TbLock fontSize="16px" color="#e67700" />
                <Text fz="12px" c="#856404">
                  Automation is locked — view only. Reviewer configuration cannot be changed while
                  the period is locked.
                </Text>
              </Flex>
            )}
            <Text fz="13px" c="dimmed" mb="16px">
              {isLocked ? (
                'Review who has been assigned as a reviewer and their approval status.'
              ) : (
                <>
                  Add reviewers, set what each can see (<b>View only</b>) or must approve (
                  <b>Approve</b>) across {isFormPF ? 'Fund' : 'AIF'} and{' '}
                  {isFormPF ? 'Managers' : 'AIFM'}, and track their sign-off.
                </>
              )}
            </Text>
            <div
              style={{ border: '0.5px solid #f1f3f5', borderRadius: '12px', overflow: 'hidden' }}
            >
              {rows.map((item, idx) => {
                const opened = selectedUser?.id === item.id;
                const aifWord = isFormPF ? 'Fund' : 'AIF';
                const aifmWord = isFormPF ? 'Managers' : 'AIFM';
                const aifLevel = getAifLevel(item);
                const aifmLevel = getAifmLevel(item);
                const fundCount = item.fundIds?.length || 0;
                const levelIcon = lvl =>
                  lvl === 'approve' ? (
                    <TbShieldCheck size={13} />
                  ) : lvl === 'view' ? (
                    <TbEye size={13} />
                  ) : (
                    <TbCircleOff size={13} />
                  );
                const levelData = [
                  { value: 'none', label: 'None' },
                  { value: 'view', label: 'View only' },
                  { value: 'approve', label: 'Approve' }
                ];
                // Approval state (for sent reviewers) is shown *inside* the access badge via colour +
                // icon — green = approved, orange = pending — instead of a separate counter chip.
                const dashRow = statusById[item.approverId];
                const sentRow = !!item.approverId && !!item.invitationSent;
                // AIF approval is a fraction (N of M funds) → badge shows a progress ring + "N/M".
                const aifApprovalActive = sentRow && item.approvalRequired;
                const aifTotal = dashRow?.fund_ids?.length || fundCount;
                const aifApprovedCount = (dashRow?.fund_ids || []).filter(
                  f => f.is_approved
                ).length;
                const aifDone = aifApprovalActive && aifTotal > 0 && aifApprovedCount === aifTotal;
                // AIFM approval is a single report → binary (approved / pending).
                const aifmApprovalState =
                  sentRow && item.aifmApprovalRequired
                    ? dashRow?.is_aifm_approved
                      ? 'approved'
                      : 'pending'
                    : null;
                const stateColor = st =>
                  st === 'approved' ? 'green' : st === 'pending' ? 'orange' : null;
                const stateIcon = (st, fallback) =>
                  st === 'approved' ? (
                    <TbCircleCheck size={13} />
                  ) : st === 'pending' ? (
                    <TbClock size={13} />
                  ) : (
                    fallback
                  );
                // Per-fund breakdown on the collapsed row — "2 approve · 1 view"
                // (or the approval progress ring once the reviewer is invited).
                // Derived from row.fundLevels (falls back to fundIds + uniform
                // level for rows that predate the per-fund model).
                const rowLevels =
                  item.fundLevels && Object.keys(item.fundLevels).length > 0
                    ? item.fundLevels
                    : Object.fromEntries(
                        (item.fundIds || []).map(fid => [
                          fid,
                          item.approvalRequired ? 'approve' : 'view'
                        ])
                      );
                const rowApproveCount = Object.values(rowLevels).filter(
                  l => l === 'approve'
                ).length;
                const rowViewCount = Object.values(rowLevels).filter(l => l === 'view').length;
                const rowLevelSummaryLabel = () => {
                  const parts = [];
                  if (rowApproveCount) parts.push(`${rowApproveCount} approve`);
                  if (rowViewCount) parts.push(`${rowViewCount} view`);
                  return parts.join(' · ');
                };
                // Chip totals ALWAYS come from local `rowLevels` so unsaved
                // level edits are reflected immediately (previously the chip
                // read from `dashRow.fund_ids` — server truth — and looked
                // stale until Save + refetch landed). Server data is only
                // consulted for the "approved so far" count, and even then
                // only for funds that are LOCALLY at approve level.
                const approvedByServer = new Set(
                  (dashRow?.fund_ids || []).filter(f => f.is_approved).map(f => f.fund_id)
                );
                const approveTotalNow = rowApproveCount;
                const viewTotalNow = rowViewCount;
                const approvedNow = Object.keys(rowLevels).filter(
                  fid => rowLevels[fid] === 'approve' && approvedByServer.has(fid)
                ).length;
                const buildAifChips = () => {
                  const out = [];
                  if (approveTotalNow > 0) {
                    if (sentRow) {
                      const done = approvedNow === approveTotalNow;
                      out.push({
                        icon: done ? (
                          <TbCircleCheck size={13} />
                        ) : (
                          progressRing(approvedNow, approveTotalNow)
                        ),
                        label: `${approvedNow}/${approveTotalNow} approved`,
                        color: done ? 'green' : 'orange'
                      });
                    } else {
                      out.push({
                        icon: <TbShieldCheck size={13} />,
                        label: `${approveTotalNow} approve`,
                        color: null
                      });
                    }
                  }
                  if (viewTotalNow > 0) {
                    out.push({
                      icon: <TbEye size={13} />,
                      label: `${viewTotalNow} view`,
                      color: null
                    });
                  }
                  if (out.length === 0) {
                    out.push({
                      icon: <TbCircleOff size={13} />,
                      label: `No ${isFormPF ? 'funds' : 'AIFs'}`,
                      color: null
                    });
                  }
                  return out;
                };
                const aifChips = buildAifChips();
                const aifmChip =
                  aifmLevel === 'none'
                    ? { icon: <TbCircleOff size={13} />, label: `No ${aifmWord}`, color: null }
                    : aifmApprovalState
                      ? {
                          icon: stateIcon(aifmApprovalState, null),
                          label: `${aifmWord} · ${aifmApprovalState}`,
                          color: stateColor(aifmApprovalState)
                        }
                      : {
                          icon: levelIcon(aifmLevel),
                          label: `${aifmWord} · ${aifmLevel}`,
                          color: null
                        };
                return (
                  <Fragment key={item.id}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '14px 18px',
                        borderTop: idx === 0 ? 'none' : '0.5px solid #f1f3f5'
                      }}
                    >
                      {/* Draft-selection checkbox — rendered ONLY when at
                          least one draft exists in the list, and only on the
                          draft rows themselves. Feeds the "Send drafts (N)"
                          button which stays disabled until at least one is
                          ticked. Non-draft rows leave the slot empty (no
                          disabled placeholder) so the row layout stays clean.
                          Hidden entirely on locked periods along with every
                          other mutation control. */}
                      {!isLocked && draftApprovers.length > 0 && (
                        <div style={{ width: 20, flexShrink: 0 }}>
                          {!!item.approverId && !item.invitationSent && (
                            <Checkbox
                              size="xs"
                              aria-label={`Select draft ${item.name || 'reviewer'}`}
                              checked={selectedDraftIds.includes(item.approverId)}
                              onChange={() => toggleDraftSelection(item.approverId)}
                            />
                          )}
                        </div>
                      )}
                      {item.approverId ? (
                        <div style={{ width: 396, flexShrink: 0 }}>
                          <Flex align="center" gap="8px">
                            <div style={{ fontSize: 14, color: '#25262b', lineHeight: '18px' }}>
                              {item.name}
                            </div>
                            {/* "Draft" badge — surfaces reviewers who have
                                been persisted (`approverId` exists) but
                                whose invitation email hasn't been sent
                                (`invitation_sent === false`). Fades out
                                automatically the next render after
                                `invitePending` succeeds and the members
                                refetch flips `invitationSent` to true. */}
                            {!item.invitationSent && (
                              <Badge
                                size="xs"
                                radius="sm"
                                variant="light"
                                color="gray"
                                styles={{ label: { textTransform: 'none', fontWeight: 500 } }}
                              >
                                Draft
                              </Badge>
                            )}
                          </Flex>
                          <div style={{ fontSize: 12, color: '#868e96', lineHeight: '16px' }}>
                            {item.email}
                          </div>
                        </div>
                      ) : (
                        <>
                          <Input
                            size="xs"
                            placeholder="Reviewer name"
                            value={item.name}
                            onChange={e => onChange(e, item.id)}
                            name="name"
                            leftSection={<TbUser fontSize="15px" />}
                            style={{ width: 180 }}
                          />
                          <TextInput
                            size="xs"
                            placeholder="name@company.com"
                            type="email"
                            value={item.email}
                            onChange={e => onChange(e, item.id)}
                            name="email"
                            error={item.email && !validateEmail(item.email) ? 'Invalid email' : ''}
                            leftSection={<TbMail fontSize="15px" />}
                            style={{ width: 200 }}
                          />
                        </>
                      )}
                      {/* Chips stay visible whether the row is collapsed OR
                          expanded — they're the at-a-glance summary of what
                          the reviewer has access to, and hiding them while
                          the settings panel is open forces the user to scan
                          the panel to remember the totals. */}
                      <Flex gap="8px" align="center" wrap="nowrap">
                        {(() => {
                          // Only show a chip for parts the reviewer actually has access to; a "No AIFs"/
                          // "No AIFM" chip is just noise. If they have nothing, one red "No access" flag.
                          const chips = [];
                          if (aifLevel !== 'none') chips.push(...aifChips);
                          if (aifmLevel !== 'none') chips.push(aifmChip);
                          if (chips.length === 0)
                            chips.push({
                              icon: <TbCircleOff size={13} />,
                              label: 'No access',
                              color: 'red'
                            });
                          return chips.map((c, i) => (
                            <Badge
                              key={i}
                              size="md"
                              radius="md"
                              variant={c.color ? 'light' : 'default'}
                              color={c.color || undefined}
                              leftSection={<Flex align="center">{c.icon}</Flex>}
                              styles={{
                                label: {
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  fontSize: '13px'
                                },
                                root: {
                                  flexShrink: 0,
                                  height: 26,
                                  paddingLeft: 11,
                                  paddingRight: 11,
                                  ...(c.color ? {} : { color: '#495057' })
                                }
                              }}
                            >
                              {c.label}
                            </Badge>
                          ));
                        })()}
                      </Flex>
                      <Flex gap="10px" align="center" wrap="nowrap" style={{ marginLeft: 'auto' }}>
                        <ActionIcon
                          variant={opened ? 'light' : 'subtle'}
                          color={opened ? 'blue' : 'gray'}
                          size="md"
                          w={40}
                          aria-label="Edit access"
                          onClick={() => (opened ? onSaveFunds() : handleSelectFunds(item))}
                        >
                          <Flex align="center" gap="1px">
                            <TbSettings fontSize="17px" />
                            <TbChevronDown
                              fontSize="10px"
                              style={{ transform: opened ? 'rotate(180deg)' : 'none' }}
                            />
                          </Flex>
                        </ActionIcon>
                        {!isLocked && (
                          <ActionIcon variant="white" size="sm" aria-label="Remove reviewer">
                            <TbTrash
                              onClick={() => onDeleteRow(item)}
                              fontSize="16px"
                              color="#CED4DA"
                            />
                          </ActionIcon>
                        )}
                      </Flex>
                    </div>
                    {opened &&
                      (() => {
                        const total = inputFunds.data?.length || 0;
                        const manyFunds = total > 8;
                        const fundWord = isFormPF ? 'Funds' : 'AIFs';
                        const partLabel = {
                          flexShrink: 0,
                          width: 44,
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#495057'
                        };
                        // Approval progress (read-only) for a sent reviewer.
                        const dash = statusById[selectedUser.approverId];
                        const sent = !!selectedUser.invitationSent;
                        const fundApproved = fid =>
                          !!(dash?.fund_ids || []).find(f => f.fund_id === fid)?.is_approved;
                        const statusDot = approved => (
                          <Flex
                            align="center"
                            gap="5px"
                            c={approved ? 'green' : 'orange'}
                            style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                          >
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: 'currentColor',
                                flexShrink: 0
                              }}
                            />
                            {approved ? 'Approved' : 'Pending'}
                          </Flex>
                        );
                        // Per-fund summary counts driven by fundLevels (fund
                        // missing → 'none'). Feed the footer + reviewer chip.
                        let approveN = 0;
                        let viewN = 0;
                        (inputFunds.data || []).forEach(f => {
                          const lvl = fundLevels[f.id];
                          if (lvl === 'approve') approveN += 1;
                          else if (lvl === 'view') viewN += 1;
                        });
                        const noneN = total - approveN - viewN;
                        // Standard 3-state segmented control (used for AIFM
                        // and, in a compact icon-only variant, per-fund below).
                        // `disabled` propagated from `isLocked` — control
                        // stays visible but no longer accepts input.
                        const levelBtn = (value, options, onChange) => (
                          <SegmentedControl
                            size="xs"
                            value={value}
                            onChange={onChange}
                            data={options}
                            disabled={isLocked}
                            styles={{ label: { padding: '3px 12px', fontSize: '12px' } }}
                          />
                        );
                        // Per-fund segmented control — icon + text label so
                        // the vocabulary is discoverable without hovering.
                        // Tooltip still carries the longer description on
                        // hover for context. withinPortal keeps the tooltip
                        // out of the modal's overflow clipping.
                        const levelTooltip = (label, description, node) => (
                          <Tooltip
                            label={
                              <>
                                <div style={{ fontWeight: 600 }}>{label}</div>
                                <div style={{ opacity: 0.85 }}>{description}</div>
                              </>
                            }
                            withArrow
                            withinPortal
                            openDelay={200}
                            position="top"
                          >
                            {node}
                          </Tooltip>
                        );
                        const perFundLevelData = [
                          {
                            value: 'none',
                            label: levelTooltip(
                              'None',
                              `Reviewer has no access to this ${aifWord.toLowerCase()}.`,
                              <Flex align="center" gap="6px">
                                <TbCircleOff size={13} />
                                <span>None</span>
                              </Flex>
                            )
                          },
                          {
                            value: 'view',
                            label: levelTooltip(
                              'View only',
                              `Reviewer can see this ${aifWord.toLowerCase()} but doesn’t need to approve.`,
                              <Flex align="center" gap="6px">
                                <TbEye size={13} />
                                <span>View</span>
                              </Flex>
                            )
                          },
                          {
                            value: 'approve',
                            label: levelTooltip(
                              'Approve',
                              `Reviewer must approve this ${aifWord.toLowerCase()} before the report can be submitted.`,
                              <Flex align="center" gap="6px">
                                <TbShieldCheck size={13} />
                                <span>Approve</span>
                              </Flex>
                            )
                          }
                        ];
                        return (
                          <div
                            style={{
                              padding: '16px 18px 18px',
                              background: '#fbfbfb',
                              borderTop: '0.5px solid #f1f3f5'
                            }}
                          >
                            {/* AIF section header — presets + search live here. */}
                            <Flex align="center" gap="12px" mb="8px">
                              <span style={partLabel}>{aifWord}</span>
                              {/* Presets menu — one-click apply-to-all.
                                  Hidden on locked periods along with every
                                  other mutation entry point. */}
                              {!isLocked && (
                                <Menu withinPortal position="bottom-start" shadow="md">
                                  <Menu.Target>
                                    <Button
                                      size="xs"
                                      variant="default"
                                      rightSection={<TbChevronDown size={12} />}
                                    >
                                      Presets
                                    </Button>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    <Menu.Item
                                      leftSection={<TbShieldCheck size={14} />}
                                      onClick={() => applyPreset('approve')}
                                    >
                                      Approve all
                                    </Menu.Item>
                                    <Menu.Item
                                      leftSection={<TbEye size={14} />}
                                      onClick={() => applyPreset('view')}
                                    >
                                      View all
                                    </Menu.Item>
                                    <Menu.Item
                                      leftSection={<TbCircleOff size={14} />}
                                      onClick={() => applyPreset('none')}
                                    >
                                      None
                                    </Menu.Item>
                                  </Menu.Dropdown>
                                </Menu>
                              )}
                              <Flex align="center" gap="12px" style={{ marginLeft: 'auto' }}>
                                {manyFunds || showFundSearch ? (
                                  <TextInput
                                    size="xs"
                                    autoFocus={showFundSearch}
                                    placeholder="Search"
                                    value={term || ''}
                                    onChange={e => onChangeTerm(e.target.value)}
                                    leftSection={<TbSearch fontSize="13px" />}
                                    rightSection={
                                      !manyFunds && (
                                        <ActionIcon
                                          variant="subtle"
                                          color="gray"
                                          size="xs"
                                          aria-label="Close search"
                                          onClick={() => {
                                            setShowFundSearch(false);
                                            onChangeTerm(null);
                                          }}
                                        >
                                          <TbX fontSize="13px" />
                                        </ActionIcon>
                                      )
                                    }
                                    style={{ width: 190 }}
                                  />
                                ) : (
                                  <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="sm"
                                    aria-label="Search funds"
                                    onClick={() => setShowFundSearch(true)}
                                  >
                                    <TbSearch fontSize="15px" />
                                  </ActionIcon>
                                )}
                              </Flex>
                            </Flex>

                            {/* Fund table — name · per-fund segmented
                                control · approval status dot. Bulk multi-
                                select was dropped — Presets menu above covers
                                the "apply to all" use case; per-fund clicks
                                cover the rest. */}
                            <div style={{ marginBottom: 14 }}>
                              <FundContainer>
                                <div className="group" style={{ marginTop: 0 }}>
                                  <div className="scroll">
                                    {_inputFunds?.length ? (
                                      <>
                                        {/* Column header row. */}
                                        <div
                                          className="item"
                                          style={{
                                            padding: '6px 12px',
                                            fontSize: 11,
                                            color: '#868e96',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.4,
                                            background: '#f8f9fa',
                                            borderBottom: '0.5px solid #e9ecef'
                                          }}
                                        >
                                          <span>{fundWord}</span>
                                          <Flex align="center" gap="16px">
                                            <span>Level</span>
                                            <span style={{ minWidth: 76, textAlign: 'left' }}>
                                              Status
                                            </span>
                                          </Flex>
                                        </div>
                                        {_inputFunds.map(f => {
                                          const cur = fundLevels[f.id] || 'none';
                                          return (
                                            <div key={f.id} className="item">
                                              <span>{f.name}</span>
                                              <Flex align="center" gap="16px">
                                                {levelBtn(cur, perFundLevelData, lvl =>
                                                  setFundLevel(f.id, lvl)
                                                )}
                                                <div style={{ minWidth: 76, textAlign: 'left' }}>
                                                  {sent && cur === 'approve' ? (
                                                    statusDot(fundApproved(f.id))
                                                  ) : (
                                                    <span style={{ color: '#ced4da' }}>—</span>
                                                  )}
                                                </div>
                                              </Flex>
                                            </div>
                                          );
                                        })}
                                      </>
                                    ) : (
                                      <div className="empty">
                                        No {fundWord.toLowerCase()} match “{term}”
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </FundContainer>
                              <span
                                style={{
                                  display: 'block',
                                  marginTop: 8,
                                  fontSize: 12,
                                  color: '#adb5bd'
                                }}
                              >
                                {approveN} approve · {viewN} view · {noneN} none
                              </span>
                            </div>

                            <div style={{ borderTop: '1px solid #f1f3f5', margin: '0 0 14px' }} />

                            {/* AIFM — single report toggle. */}
                            <Flex align="center" gap="12px">
                              <span style={partLabel}>{aifmWord}</span>
                              {levelBtn(aifmLevel, levelData, lvl => setAifmLevel(item, lvl))}
                              {sent && selectedUser.aifmApprovalRequired && (
                                <Flex style={{ marginLeft: 'auto' }}>
                                  {statusDot(!!dash?.is_aifm_approved)}
                                </Flex>
                              )}
                            </Flex>

                            {/* Save/Cancel — appears only for existing reviewers
                                with unsaved edits vs the server snapshot. On
                                locked periods the SegmentedControls are
                                disabled so nothing dirties, but the extra
                                `!isLocked` guard is a belt-and-braces block
                                in case any local state slips through. */}
                            {!isLocked && isRowDirty(item) && (
                              <Flex
                                justify="flex-end"
                                gap="8px"
                                mt="14px"
                                pt="12px"
                                style={{ borderTop: '1px dashed #f1f3f5' }}
                              >
                                <Button
                                  size="xs"
                                  variant="default"
                                  onClick={() => cancelRow(item)}
                                  disabled={savingRow === item.id}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="xs"
                                  onClick={() => saveRow(item)}
                                  loading={savingRow === item.id}
                                >
                                  Save changes
                                </Button>
                              </Flex>
                            )}
                          </div>
                        );
                      })()}
                  </Fragment>
                );
              })}
              {!isLocked && (
                <Button
                  onClick={onAddRow}
                  leftSection={<TbPlus fontSize="15px" />}
                  variant="subtle"
                  fullWidth
                  styles={{
                    root: {
                      height: 44,
                      borderTop: '0.5px solid #f1f3f5',
                      borderRadius: 0
                    },
                    inner: { justifyContent: 'flex-start' },
                    label: { fontWeight: 500 }
                  }}
                >
                  Add reviewer
                </Button>
              )}
            </div>
          </div>
          {!isLocked && (
            <Flex
              justify="flex-end"
              m="0"
              align="center"
              gap="16px"
              mt="20px"
              style={{ flexShrink: 0 }}
            >
              {/* Send drafts — only rendered when there's actually a batch
                  to send. Sits LEFT of "Save as draft" so the sequence
                  reads left→right as the workflow progression: send the
                  existing drafts, save new ones as drafts, or send new
                  ones immediately for review. */}
              {/* Any active mutation locks all three footer buttons so a user
                  can't fire "Save as draft" mid-"Send drafts" (or vice versa)
                  and trigger duplicate/racing writes. */}
              {(() => {
                const anyMutationPending = invite.isLoading || invitePending.isLoading;
                return (
                  <>
                    {draftApprovers.length > 0 && (
                      <Button
                        size="xs"
                        variant="outline"
                        color="blue"
                        onClick={handleSendDrafts}
                        loading={invitePending.isLoading}
                        disabled={anyMutationPending || selectedDrafts.length === 0}
                      >
                        Send drafts ({selectedDrafts.length}) for review
                      </Button>
                    )}
                    <Button
                      loading={!isSendImmediately && invite.isLoading}
                      onClick={() => onSave()}
                      size="xs"
                      variant="outline"
                      color="gray"
                      disabled={newMembers.length === 0 || disabledSendButton || anyMutationPending}
                    >
                      Save as draft
                    </Button>
                    <Button
                      loading={isSendImmediately && (invite.isLoading || invitePending.isLoading)}
                      onClick={() => onSave(true)}
                      size="xs"
                      disabled={newMembers.length === 0 || disabledSendButton || anyMutationPending}
                    >
                      Send for review
                    </Button>
                  </>
                );
              })()}
            </Flex>
          )}
        </BodyStyle>
      </Modal>
      <DeleteConfirmModal
        title="Removing Reviewer"
        description="Are you sure you want to remove this reviewer? They will lose access to all the funds you’ve assigned for review."
        isOpen={deletableMemberId}
        onClose={() => setDeletableMemberId(null)}
        submitLabel="Yes, Remove Reviewer"
        cancelLabel="Cancel"
        onConfirm={onDeleteMember}
        isLoading={deleteApprover.isLoading}
      />
      {/* Fires when the user closes the drawer with unsaved edits. */}
      <DeleteConfirmModal
        title="Unsaved changes"
        description="You have unsaved changes on one or more reviewers. Discard them and close?"
        isOpen={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        submitLabel="Discard and close"
        cancelLabel="Keep editing"
        onConfirm={confirmCloseDiscard}
      />
    </>
  );
}

const BodyStyle = styled.div`
  display: flex;
  flex-direction: column;
  height: min(72vh, 460px);
  .rev-scroll {
    scrollbar-width: thin;
    scrollbar-color: #dee2e6 transparent;
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: #dee2e6;
      border-radius: 8px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #ced4da;
      background-clip: padding-box;
    }
  }
`;

const FundContainer = styled.div`
  .group {
    border: 1px solid #f1f3f5;
    border-radius: 8px;
    overflow: hidden;
  }
  .group-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 12px;
    min-height: 34px;
    border-bottom: 1px solid #f1f3f5;
  }
  .count {
    color: #adb5bd;
    font-size: 12px;
  }
  /* Inner list intentionally has NO max-height / overflow — the outer
     .rev-scroll owns scrolling for the entire modal body, so a nested
     scroll here would create the double-scroll trap. */
  .scroll {
  }
  .item {
    padding: 6px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: #000;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    border-bottom: 1px solid #f1f3f5;
    &:last-child {
      border-bottom: none;
    }
  }
  .empty {
    padding: 16px 12px;
    color: #adb5bd;
    font-size: 13px;
    text-align: center;
  }
`;

const SelectFundsCell = styled.div`
  color: #228be6;
`;

const WideDrawer = styled(Drawer)`
  width: 730px;
`;
